import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import {
  ButtonComponent as Button,
  ButtonMode,
  Box,
  BenefitType,
  InfoIcon,
  Text,
  Tooltip,
  Checkbox
} from 'edenred-ui';

import { IBenefitTypeTile } from '@epi-models/containers/ManageBenefits';
import { LUNCH } from '@epi-constants/benefitTypes';
import { useGetDetailsQuery } from '@epi-api/user';
import { useOnboardingWizard } from '@epi-shared/hooks';

import { BenefitTypeIcon } from '../BenefitTypeIcon';

import {
  BenefitTypeName,
  BenefitTypeStatus,
  BenefitTypeStatusText,
  BenefitTypeTileContainer,
  CheckboxWrapper,
  MyChoiceSubtitle,
  StyledLink
} from './BenefitTypeTile.styles';

const Strong = styled.span`
  font-weight: ${props => props.theme.fontSemiBold};
`;

export const BenefitTypeTile = ({
  benefit,
  active,
  checked,
  onToggle
}: IBenefitTypeTile) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const { pathname } = useLocation();
  const { isOnBoardingActive } = useOnboardingWizard();
  const { secondaryFrontColor, primaryFrontColor, sizeExtraLarge } = useTheme();
  const { data: customerData } = useGetDetailsQuery();
  const benefitName = t(`components.manage_benefits.new.benefits.${benefit}`);

  const isOnboardingDone =
    customerData?.isBenefitsSet &&
    customerData?.isEmployeesSet &&
    (customerData?.isMoneyTransfered || customerData?.isLoadInitiated);

  const showInfo =
    (benefit == BenefitType.massage || benefit == BenefitType.wellbeing) &&
    !isOnboardingDone;

  const showMerchantToolLink =
    (benefit == BenefitType.massage || benefit == BenefitType.wellbeing) &&
    active &&
    isOnboardingDone;

  const buttonText = active
    ? t('components.manage_benefits.new.view_groups')
    : t('components.manage_benefits.new.activate');
  const handleClick = () => {
    push(`${pathname}/${benefit}`);
  };

  const handleTileClick = () => {
    if (!active) {
      onToggle?.(benefit);
    }
  };

  return (
    <BenefitTypeTileContainer
      active={active}
      id={`benefit-tile_${benefitName}`}
      checked={checked}
      onClick={handleTileClick}
    >
      {!active && (
        <CheckboxWrapper>
          <Checkbox checked={checked} />
        </CheckboxWrapper>
      )}

      {active && (
        <BenefitTypeStatus id={`status-active-${benefit}`}>
          <BenefitTypeStatusText>
            {t('components.manage_benefits.new.active')}
          </BenefitTypeStatusText>
        </BenefitTypeStatus>
      )}
      <BenefitTypeIcon benefit={benefit} />
      <BenefitTypeName
        id={`benefit-tile_${benefitName}-title`}
        isLunchSelected={benefit === LUNCH.toLowerCase()}
      >
        <Box display="flex">
          <Box>{benefitName}</Box>
          {showInfo && (
            <Box pl={0.5}>
              <Tooltip
                arrow
                placement="top"
                componentsProps={{ tooltip: { sx: { maxWidth: '420px' } } }}
                title={
                  <Box>
                    <Text small>
                      <Trans
                        i18nKey="components.manage_benefits.new.onboarding_info_tooltip.info"
                        components={{
                          strong: <Strong />
                        }}
                        values={{
                          benefit: t(
                            `components.manage_benefits.new.onboarding_info_tooltip.${benefit}.benefit`
                          )
                        }}
                      />
                    </Text>
                  </Box>
                }
              >
                <Box
                  display="inline-block"
                  width={sizeExtraLarge}
                  height={sizeExtraLarge}
                >
                  <InfoIcon
                    width={sizeExtraLarge}
                    height={sizeExtraLarge}
                    color={primaryFrontColor}
                  />
                </Box>
              </Tooltip>
            </Box>
          )}
        </Box>
      </BenefitTypeName>
      {benefit !== LUNCH.toLowerCase() && (
        <MyChoiceSubtitle isLunchSelected={benefit === LUNCH.toLowerCase()}>
          {t('components.benefit_tile.my_choice_subtitle')}
        </MyChoiceSubtitle>
      )}

      {active && !isOnBoardingActive && (
        <Box maxWidth={248} width="100%" pb={showMerchantToolLink ? 0 : 5}>
          <Button
            fullWidth
            id={`view-groups-${benefit}`}
            onClick={handleClick}
            mode={active ? ButtonMode.Secondary : ButtonMode.Primary}
          >
            {buttonText}
          </Button>
        </Box>
      )}
      {showMerchantToolLink && (
        <Box pt={2} pb={0.5}>
          <StyledLink
            target="_blank"
            href={t(
              `components.manage_benefits.new.select_merchants.${benefit}.url`
            )}
          >
            {t('components.manage_benefits.new.select_merchants.text')}
          </StyledLink>
          <Box display="inline-block" pl={0.5}>
            <Tooltip
              arrow
              placement="top"
              componentsProps={{ tooltip: { sx: { maxWidth: '720px' } } }}
              title={
                <Box pr={1}>
                  <Text small>
                    <Trans
                      i18nKey="components.manage_benefits.new.select_merchants.info_tooltip.info"
                      components={{
                        strong: <Strong />
                      }}
                      values={{
                        benefit: t(
                          `components.manage_benefits.new.select_merchants.${benefit}.benefit_network`
                        )
                      }}
                    />
                  </Text>
                </Box>
              }
            >
              <Box display="inline-block" width={16} height={16}>
                <InfoIcon width={16} height={16} color={secondaryFrontColor} />
              </Box>
            </Tooltip>
          </Box>
        </Box>
      )}
    </BenefitTypeTileContainer>
  );
};
